import { zxcvbn, zxcvbnOptions } from '@zxcvbn-ts/core';
import * as CommonLanguage from '@zxcvbn-ts/language-common';
import * as EnglishLanguage from '@zxcvbn-ts/language-en';
import type { ZxcvbnResult } from '@zxcvbn-ts/core';

export const MaximumPasswordLength = 1023;
export const PasswordPolicyMinimumCrackTimeYears = 100;
export const SecondsPerPasswordPolicyYear = 365.25 * 24 * 60 * 60;
export const PasswordPolicyMinimumCrackTimeSeconds =
    PasswordPolicyMinimumCrackTimeYears * SecondsPerPasswordPolicyYear;
export const DisallowedDefaultPassword = 'TestPassword123!';

export const PasswordPolicyRejectionReason = {
    PasswordTooLong: 'PASSWORD_TOO_LONG',
    DisallowedDefaultPassword: 'DISALLOWED_DEFAULT_PASSWORD',
    ZxcvbnTooWeak: 'ZXCVBN_TOO_WEAK',
} as const;

export type PasswordPolicyRejectionReason =
    (typeof PasswordPolicyRejectionReason)[keyof typeof PasswordPolicyRejectionReason];

export interface PasswordPolicyEvaluationInput {
    password: string;
    userInputs?: readonly string[];
}

export interface PasswordPolicyZxcvbnEstimate {
    score: number;
    guesses: number;
    guesses_log10: number;
    offline_slow_hashing_1e4_per_second_seconds: number;
    offline_slow_hashing_1e4_per_second_display: string;
}

export interface PasswordPolicyEvaluation {
    accepted: boolean;
    rejected_reasons: PasswordPolicyRejectionReason[];
    zxcvbn_estimate: PasswordPolicyZxcvbnEstimate | null;
}

let zxcvbnConfigured = false;

function configureZxcvbn(): void {
    if (zxcvbnConfigured) {
        return;
    }

    zxcvbnOptions.setOptions({
        translations: EnglishLanguage.translations,
        graphs: CommonLanguage.adjacencyGraphs,
        dictionary: {
            ...CommonLanguage.dictionary,
            ...EnglishLanguage.dictionary,
        },
    });
    zxcvbnConfigured = true;
}

export function evaluatePasswordPolicy(
    input: PasswordPolicyEvaluationInput
): PasswordPolicyEvaluation {
    const rejectedReasons: PasswordPolicyRejectionReason[] = [];

    if (input.password.length > MaximumPasswordLength) {
        rejectedReasons.push(PasswordPolicyRejectionReason.PasswordTooLong);
        return {
            accepted: false,
            rejected_reasons: rejectedReasons,
            zxcvbn_estimate: null,
        };
    }

    configureZxcvbn();
    const userInputs: string[] = [...(input.userInputs ?? [])];
    const result: ZxcvbnResult = zxcvbn(input.password, userInputs);
    const slowHashingCrackTimeSeconds: number =
        result.crackTimesSeconds.offlineSlowHashing1e4PerSecond;

    if (input.password === DisallowedDefaultPassword) {
        rejectedReasons.push(PasswordPolicyRejectionReason.DisallowedDefaultPassword);
    }

    if (slowHashingCrackTimeSeconds < PasswordPolicyMinimumCrackTimeSeconds) {
        rejectedReasons.push(PasswordPolicyRejectionReason.ZxcvbnTooWeak);
    }

    return {
        accepted: rejectedReasons.length === 0,
        rejected_reasons: rejectedReasons,
        zxcvbn_estimate: {
            score: result.score,
            guesses: result.guesses,
            guesses_log10: result.guessesLog10,
            offline_slow_hashing_1e4_per_second_seconds: slowHashingCrackTimeSeconds,
            offline_slow_hashing_1e4_per_second_display:
                result.crackTimesDisplay.offlineSlowHashing1e4PerSecond,
        },
    };
}
